/**
 * Vista de recuperación de contraseña con formulario de email.
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, Typography, Image, Space, Alert, message } from "antd";
import logoPrincipal from "assets/LogoFull.svg";
import POSTCambiarClave from "helpers/auth/POSTCambiarClave";

const { Text } = Typography;

const CenteredCard = ({ children }) => {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #323443 0%, #791ea3 70%, #a173c5 100%)",
      }}
    >
      <Card
        bordered={false}
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          boxShadow: "0 20px 40px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.3)",
          backdropFilter: "blur(12px)",
        }}
      >
        {children}
      </Card>
    </div>
  );
};

const RecuperarContrasenia = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const username = params.get("username");

  const [loading, setLoading] = useState(false);
  const [tokenValido, setTokenValido] = useState(false);

  // Validar token al cargar
  useEffect(() => {
    // Validar que el token tenga exactamente 6 caracteres alfanuméricos
    const tokenRegex = /^[A-Za-z0-9]{6}$/;
    const esValido = token && tokenRegex.test(token);
    setTokenValido(esValido);
  }, [token]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const payload = {
        usuario: username,
        clave: values.password,
        claveDuplicada: values.confirm,
        token: token,
      };

      const response = await POSTCambiarClave(payload);

      if (response && response.resultado === 1) {
        message.success(response?.mensaje || "Contraseña cambiada exitosamente.");
        setTimeout(() => {
          setLoading(false);
          // Redirigir al login después de éxito
          navigate("/");
        }, 1500);
      } else {
        message.error("Se produjo un error al cambiar la contraseña.");
      }
    } catch (error) {
      message.error("Error al cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CenteredCard>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Image src={logoPrincipal} preview={false} width={250} />
        </div>

        {!tokenValido || !username ? (
          // Token inválido - mostrar mensaje de error
          <>
            <Alert
              type="error"
              showIcon
              description="No es posible realizar la recuperación de contraseña."
            />
            <Button
              type="primary"
              block
              size="large"
              onClick={() => navigate("/")}
              style={{
                height: 44,
                borderRadius: 10,
                letterSpacing: 0.3,
              }}
            >
              Volver al inicio
            </Button>
          </>
        ) : (
          // Token válido - mostrar formulario
          <>
            <div>
              <div style={{ gap: 8, display: "flex", flexDirection: "column" }}>
                <Text strong style={{ marginBottom: 0 }}>
                  Establece una nueva contraseña
                </Text>
                <Text type="secondary" style={{ marginBottom: 0 }}>
                  Debe contener al menos una mayúscula, una minúscula, un número y al menos 8
                  caracteres.
                </Text>
              </div>
            </div>

            <Form layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa tu contraseña",
                  },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
                    message: "Debe tener 8+ caracteres, 1 mayúscula, 1 minúscula y 1 número",
                  },
                ]}
              >
                <Input.Password
                  size="large"
                  style={{
                    borderRadius: 10,
                  }}
                />
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Repetir contraseña"
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: "Por favor confirma tu contraseña",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Las contraseñas no coinciden"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  style={{
                    borderRadius: 10,
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                style={{
                  marginTop: 8,
                  height: 44,
                  borderRadius: 10,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                }}
              >
                Aplicar nueva contraseña
              </Button>
            </Form>

            <div style={{ marginTop: 8, textAlign: "center" }}>
              <Typography.Text
                className="forgot-password-text"
                style={{ color: "#791ea3", cursor: "pointer" }}
                onClick={() => navigate("/")}
              >
                Volver al inicio de sesión
              </Typography.Text>
            </div>
          </>
        )}
      </Space>
    </CenteredCard>
  );
};

export default RecuperarContrasenia;
